import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Dialog } from '@tencent/spaui';
import { getFromCombinedValue, saveCombinedValue } from '@/utils/storage';
import { FLAGS } from '@/constants/Constants';
import dayjs from 'dayjs';
import '@/common-components/HeaderBar/NewSystemDialog.scoped.less';

export interface WarnDialogConfig {
  title?: string | React.ReactNode;
  content: string | React.ReactNode;
  submitLabel: string; // 确定按钮的label
  handleSubmit?: (item: WarnDialogConfig) => void | Promise<void>; // 点击回调
  handleClose?: (item: WarnDialogConfig) => void | Promise<void>; // 点击回调
  type?: 'normal' | 'simple' | 'confirm' | 'tip';
  iconType?: 'normal' | 'simple' | 'confirm' | 'tip' | 'remind';
  useNewUI?: boolean; // 是否使用新的dialog样式
  closeButton: boolean; // 是否展示关闭按钮
  cancelButton: boolean; // 是否展示取消按钮
  className?: string;
  style?: object;
  bodyStyle?: object;
  headerStyle?: object;
  footerStyle?: object;
  id?: string; // id，用于全局查找dialog
  submitButtonProps?: object;
  cancelButtonProps?: object;
  closeButtonProps?: object;

  viewConfig: {
    saveKey: string; // 唯一key，用于存储
    saveValue?: any; // 存储的值，默认值为1
    useSessionStorage?: boolean; // 是否使用sessionStorage存储
    order?: number; // 展示顺序，0为第一展示，1为第二个展示。为空会自动增加
    showCheck?: (oldValue: any) => boolean; // 是否满足展示，不传时，策略为只展示一次。@param {oldValue} 保存的旧值。
    showCallback?: () => void; // 展示弹窗的回调
    showNextAfterClose: boolean; // 关闭后是否展示下一个，默认为false
  }
}

export interface WarnDialogManagerProps {
  readonly account: any
  readonly warnDialogConfigs: WarnDialogConfig[]
}

// 弹窗容器组件，保证某个类型的弹窗只展示一次，或按自定义规则控制展示
export const WarnDialogManager = function (props: WarnDialogManagerProps) {
  const currentIndex = useRef(-1);
  const [_currentIndex, _setCurrentIndex] = useState(-1); // 只为了触发渲染
  const [showDelay, setShowDelay] = useState(true); // 多个Dialog之间，间隔一段时间再展示
  const DELAY_TIME = 1500; // Dialog展示时间间隔

  const setCurrentIndex = useCallback((index) => {
    currentIndex.current = index;
    _setCurrentIndex(index);
  }, []);

  // 点击提交触发
  const handleSubmit = useCallback(
    async (item: WarnDialogConfig) =>  {
      if (typeof item.handleSubmit === 'function') {
        await item.handleSubmit(item);
      }
      _clickCallback(item);
    },
    [props.warnDialogConfigs, props.account],
  );

  // 关闭触发
  const handleClose = useCallback(
    async (item: WarnDialogConfig) => {
      if (typeof item.handleClose === 'function') {
        await item.handleClose(item);
      }
      _clickCallback(item);
    },
    [props.warnDialogConfigs, props.account],
  );

  // 点击回调，关闭或提交都触发
  const _clickCallback = useCallback(
    (item: WarnDialogConfig) =>  {
      saveCombinedValue(FLAGS.WARN_DIALOG_STORAGE_COMBINED_KEY, item.viewConfig.saveKey, typeof item.viewConfig.saveValue === 'undefined' ? 1 : item.viewConfig.saveValue, {
        userId: props.account.user_id,
        useSessionStorage: Boolean(item.viewConfig.useSessionStorage),
      });
      if (item.viewConfig.showNextAfterClose) {
        setShowDelay(false);
        showNextDialog();
        // 延时展示下一个
        setTimeout(() => {
          setShowDelay(true);
        }, DELAY_TIME);
      } else {
        setCurrentIndex(-1);
      }
    },
    [props.warnDialogConfigs, props.account],
  );

  // 展示下一个弹窗
  const showNextDialog = useCallback(() => {
    // 开启下一个弹窗展示
    const _tempIndex = currentIndex.current + 1;
    const _tempConfig = props.warnDialogConfigs[_tempIndex];

    // 已经检查完毕，停止检查，游标置为-1
    if (!_tempConfig) {
      setCurrentIndex(-1);
      return;
    }
    // 当前dialog不满足展示条件，则检查下一个
    if (!shouldShow(_tempConfig, props.account.user_id)) {
      setCurrentIndex(_tempIndex);
      showNextDialog();
      return;
    }

    // 满足展示条件，展示
    const showCallback = props.warnDialogConfigs[_tempIndex].viewConfig.showCallback;
    if (showCallback) {
      showCallback();
    }
    setCurrentIndex(_tempIndex);
  }, [props.warnDialogConfigs]);

  useEffect(() => {
    showNextDialog();
  }, [props.warnDialogConfigs]);

  return (
    <div>
      {props.warnDialogConfigs.map((item, index) => {
        const { type = 'normal', useNewUI = true } = item;
        return (
          <Dialog
            title={item.title}
            type={type}
            iconType={item.iconType}
            backdrop={false}
            useNewUI={useNewUI}
            key={item.viewConfig.saveKey}
            className={item.className}
            show={shouldShow(item, props.account.user_id) && showDelay && currentIndex.current === index}
            onClose={() => handleClose(item)}
            onCancel={() => handleClose(item)}
            onSubmit={() => handleSubmit(item)}
            closeButton={item.closeButton}
            cancelButton={item.cancelButton}
            submitLabel={item.submitLabel}
            style={{ width: '400px', ...(item.style || {}) }}
            id={item.id}
            submitButtonProps={item.submitButtonProps}
            cancelButtonProps={item.cancelButtonProps}
            closeButtonProps={item.closeButtonProps}
            bodyStyle={item.bodyStyle}
            headerStyle={item.headerStyle}
            footerStyle={item.footerStyle}
          >
            <div>{item.content}</div>
          </Dialog>
        );
      })}
    </div>
  );
};

// 获取是否应该展示目标弹窗
export function shouldShow(warnDialogConfig: WarnDialogConfig, userId: number): boolean {
  if (typeof warnDialogConfig.viewConfig.showCheck === 'function') {
    return warnDialogConfig.viewConfig.showCheck(getFromCombinedValue(FLAGS.WARN_DIALOG_STORAGE_COMBINED_KEY, warnDialogConfig.viewConfig.saveKey, {
      userId,
      useSessionStorage: Boolean(warnDialogConfig.viewConfig.useSessionStorage),
    }));
  }
  // 默认情况，只要有存储，就不再展示（即只展示一次）
  return !Boolean(getFromCombinedValue(FLAGS.WARN_DIALOG_STORAGE_COMBINED_KEY, warnDialogConfig.viewConfig.saveKey, {
    userId,
    useSessionStorage: Boolean(warnDialogConfig.viewConfig.useSessionStorage),
  }));
}

// 每天提醒一次的检查逻辑
export function dailyShowCheck(saveValue: string): boolean {
  if (!saveValue) {
    return true;
  }
  return dayjs().format('YYYY-MM-DD') !== saveValue;
}
